import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { azn, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Users, ArrowDownToLine, ArrowUpFromLine, TreePine, Check, X, Plus, Wallet, CreditCard } from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Icon className="w-4 h-4" /> {label}</div>
      <p className="font-heading text-2xl font-bold">{value}</p>
    </div>
  );
}

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [users, setUsers] = useState([]);
  const [trees, setTrees] = useState([]);
  const [card, setCard] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, d, w, u, t, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/deposits"), api.get("/admin/withdrawals"),
        api.get("/admin/users"), api.get("/admin/trees"), api.get("/admin/deposit-card"),
      ]);
      setStats(s.data); setDeposits(d.data); setWithdrawals(w.data); setUsers(u.data); setTrees(t.data); setCard(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-6">Admin Panel</h1>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="İstifadəçilər" value={stats?.users ?? 0} testid="admin-stat-users" />
          <StatCard icon={TreePine} label="Aktiv ağaclar" value={stats?.active_investments ?? 0} testid="admin-stat-active" />
          <StatCard icon={ArrowDownToLine} label="Depozit sorğusu" value={stats?.pending_deposits ?? 0} testid="admin-stat-dep" />
          <StatCard icon={ArrowUpFromLine} label="Çıxarış sorğusu" value={stats?.pending_withdrawals ?? 0} testid="admin-stat-wd" />
          <StatCard icon={Wallet} label="Ümumi balans" value={azn(stats?.total_user_balance)} testid="admin-stat-balance" />
        </div>

        <Tabs defaultValue="deposits">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="deposits" data-testid="admin-tab-deposits">Depozitlər</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals">Çıxarışlar</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">İstifadəçilər</TabsTrigger>
            <TabsTrigger value="trees" data-testid="admin-tab-trees">Ağaclar</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Ödəniş Kartı</TabsTrigger>
          </TabsList>

          {/* Deposits */}
          <TabsContent value="deposits" className="mt-4">
            <ReqTable
              items={deposits}
              extraCol={(r) => r.note}
              extraLabel="Qeyd"
              onApprove={(r) => act(() => api.post(`/admin/deposits/${r.req_id}/approve`), "Depozit təsdiqləndi")}
              onReject={(r) => act(() => api.post(`/admin/deposits/${r.req_id}/reject`), "Depozit rədd edildi")}
              testid="admin-deposits"
            />
          </TabsContent>

          {/* Withdrawals */}
          <TabsContent value="withdrawals" className="mt-4">
            <ReqTable
              items={withdrawals}
              extraCol={(r) => r.details}
              extraLabel="Detallar"
              onApprove={(r) => act(() => api.post(`/admin/withdrawals/${r.req_id}/approve`), "Çıxarış təsdiqləndi")}
              onReject={(r) => act(() => api.post(`/admin/withdrawals/${r.req_id}/reject`), "Çıxarış rədd edildi")}
              testid="admin-withdrawals"
            />
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="mt-4">
            {users.length === 0 ? <Empty text="İstifadəçi yoxdur" /> : (
              <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden" data-testid="admin-users-list">
                {users.map((u) => (
                  <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                    <div>
                      <p className="font-medium text-sm">{u.name || "—"} {u.role === "admin" && <Badge className="ml-1">admin</Badge>}</p>
                      <p className="text-xs text-muted-foreground">{u.email} • {u.auth_provider}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-heading font-semibold">{azn(u.balance)}</span>
                      <BalanceDialog user={u} onDone={load} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Trees */}
          <TabsContent value="trees" className="mt-4">
            <div className="flex justify-end mb-3">
              <TreeDialog onDone={load} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-trees-list">
              {trees.map((t) => (
                <div key={t.tree_id} className="rounded-2xl border border-border bg-card overflow-hidden">
                  <img src={t.image} alt={t.name} className="w-full h-28 object-cover" />
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading font-semibold">{t.name}</h3>
                      {!t.active && <Badge variant="secondary">Deaktiv</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{azn(t.price)} • {t.type === "fixed" ? `${azn(t.daily_income)}/gün` : "faiz əsaslı"}</p>
                    <div className="flex gap-2 mt-3">
                      <TreeDialog tree={t} onDone={load} />
                      {t.active && (
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(() => api.delete(`/admin/trees/${t.tree_id}`), "Ağac deaktiv edildi")} data-testid={`disable-tree-${t.tree_id}`}>Deaktiv et</Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Settings - deposit card */}
          <TabsContent value="settings" className="mt-4">
            {card ? <DepositCardForm card={card} onDone={load} /> : <div className="text-muted-foreground p-6">Yüklənir...</div>}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">{text}</div>;
}

const statusMap = {
  pending: { label: "Gözləyir", variant: "secondary" },
  approved: { label: "Təsdiqləndi", variant: "default" },
  rejected: { label: "Rədd edildi", variant: "destructive" },
};

function ReqTable({ items, extraCol, extraLabel, onApprove, onReject, testid }) {
  if (items.length === 0) return <Empty text="Sorğu yoxdur" />;
  return (
    <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid={testid}>
      {items.map((r) => {
        const st = statusMap[r.status] || { label: r.status, variant: "secondary" };
        return (
          <div key={r.req_id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5" data-testid={`req-${r.req_id}`}>
            <div className="min-w-0">
              <p className="font-heading font-semibold">{azn(r.amount)} <span className="font-sans font-normal text-sm text-muted-foreground">— {r.user_name || r.user_email}</span></p>
              <p className="text-xs text-muted-foreground truncate">{extraLabel}: {extraCol(r) || "—"} • {new Date(r.created_at).toLocaleString("az-AZ")}</p>
            </div>
            {r.status === "pending" ? (
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full" onClick={() => onApprove(r)} data-testid={`approve-${r.req_id}`}><Check className="w-4 h-4 mr-1" /> Təsdiqlə</Button>
                <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => onReject(r)} data-testid={`reject-${r.req_id}`}><X className="w-4 h-4 mr-1" /> Rədd et</Button>
              </div>
            ) : <Badge variant={st.variant}>{st.label}</Badge>}
          </div>
        );
      })}
    </div>
  );
}

function DepositCardForm({ card, onDone }) {
  const [form, setForm] = useState({
    card_number: card?.card_number || "", card_holder: card?.card_holder || "",
    bank_name: card?.bank_name || "", note: card?.note || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/deposit-card", form);
      toast.success("Ödəniş kartı yeniləndi. Saytda dərhal dəyişəcək.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-border bg-card p-6" data-testid="deposit-card-form">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center"><CreditCard className="w-4 h-4 text-primary" /></span>
          <h2 className="font-heading font-semibold">Depozit kartı</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Bu kart müştərilərin balans artırma səhifəsində dinamik göstərilir.</p>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Kart nömrəsi</Label><Input value={form.card_number} onChange={(e) => set("card_number", e.target.value)} placeholder="4169 7388 1234 5678" data-testid="card-number-input" /></div>
          <div className="space-y-1.5"><Label>Kart sahibi</Label><Input value={form.card_holder} onChange={(e) => set("card_holder", e.target.value)} placeholder="YASIL INVEST MMC" data-testid="card-holder-input" /></div>
          <div className="space-y-1.5"><Label>Bank</Label><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="Kapital Bank" data-testid="card-bank-input" /></div>
          <div className="space-y-1.5"><Label>Qeyd (müştəriyə göstəriş)</Label><Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="card-note-input" /></div>
          <Button className="rounded-full w-full" onClick={save} disabled={saving || !form.card_number || !form.card_holder} data-testid="card-save-btn">Yadda saxla</Button>
        </div>
      </div>

      <div>
        <p className="text-sm text-muted-foreground mb-2">Önizləmə (müştəri bunu görür):</p>
        <div className="rounded-xl bg-primary text-primary-foreground p-5 relative overflow-hidden max-w-sm">
          <div className="noise-overlay absolute inset-0 opacity-10" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <span className="text-xs text-primary-foreground/70 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Ödəniş kartı</span>
              <span className="text-xs text-primary-foreground/70">{form.bank_name || "—"}</span>
            </div>
            <p className="font-heading text-xl font-bold tracking-wider mt-3" data-testid="card-preview-number">{form.card_number || "—"}</p>
            <p className="text-sm text-primary-foreground/80 mt-1">{form.card_holder || "—"}</p>
            {form.note && <p className="text-xs text-primary-foreground/60 mt-2">{form.note}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function BalanceDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      await api.post(`/admin/users/${user.user_id}/balance`, { amount: parseFloat(amount), note: note || "Admin düzəlişi" });
      toast.success("Balans yeniləndi");
      setOpen(false); setAmount(""); setNote(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid={`balance-btn-${user.user_id}`}>Balans</Button>
      </DialogTrigger>
      <DialogContent data-testid="balance-dialog">
        <DialogHeader><DialogTitle className="font-heading">Balans düzəlişi — {user.name || user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Məbləğ (+ artır / − azalt)</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="məs. 100 və ya -50" data-testid="balance-amount-input" /></div>
          <div className="space-y-1.5"><Label>Qeyd</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Səbəb" data-testid="balance-note-input" /></div>
        </div>
        <DialogFooter><Button className="rounded-full" onClick={save} disabled={!amount} data-testid="balance-save-btn">Yadda saxla</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TreeDialog({ tree, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(
    tree || { name: "", price: "", type: "fixed", daily_income: "", description: "", image: "", active: true }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const payload = {
      name: form.name, price: parseFloat(form.price), type: form.type,
      daily_income: parseFloat(form.daily_income || 0), description: form.description,
      image: form.image, active: form.active !== false,
    };
    try {
      if (tree) await api.put(`/admin/trees/${tree.tree_id}`, payload);
      else await api.post("/admin/trees", payload);
      toast.success(tree ? "Ağac yeniləndi" : "Ağac əlavə edildi");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {tree
          ? <Button size="sm" variant="outline" className="rounded-full" data-testid={`edit-tree-${tree.tree_id}`}>Redaktə</Button>
          : <Button className="rounded-full" data-testid="add-tree-btn"><Plus className="w-4 h-4 mr-1" /> Ağac əlavə et</Button>}
      </DialogTrigger>
      <DialogContent data-testid="tree-dialog">
        <DialogHeader><DialogTitle className="font-heading">{tree ? "Ağacı redaktə et" : "Yeni ağac"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Ad</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tree-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Qiymət (₼)</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="tree-price-input" /></div>
            <div className="space-y-1.5"><Label>Tip</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="tree-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Sabit gündəlik</SelectItem>
                  <SelectItem value="percentage">Faiz (30-50%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.type === "fixed" && (
            <div className="space-y-1.5"><Label>Gündəlik gəlir (₼)</Label><Input type="number" step="0.01" value={form.daily_income} onChange={(e) => set("daily_income", e.target.value)} data-testid="tree-daily-input" /></div>
          )}
          <div className="space-y-1.5"><Label>Şəkil URL</Label><Input value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="tree-image-input" /></div>
          <div className="space-y-1.5"><Label>Təsvir</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="tree-desc-input" /></div>
        </div>
        <DialogFooter><Button className="rounded-full" onClick={save} disabled={!form.name || !form.price} data-testid="tree-save-btn">Yadda saxla</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
